// src/components/AdminEmergencyAlert.jsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateEmergency } from "../services/vehicleService";
import chatWS from "../services/chatWebSocket";
import EmergencyNotificationModal from "./EmergencyNotificationModal";
import { toast } from "react-toastify";
import { fetchUsers } from "../actions/UserActions";
import { fetchVehicles } from "../actions/VehicleActions";

const AdminEmergencyAlert = () => {
  const dispatch = useDispatch();
  const { userDetails, authKey, isAuthenticated } = useSelector((state) => state.auth);
  const { items: users } = useSelector((state) => state.user);
  const { items: vehicles } = useSelector((state) => state.vehicle);
  const [emergencyAlerts, setEmergencyAlerts] = useState([]);
  const [resolvingIds, setResolvingIds] = useState([]);
  const [stableUserId, setStableUserId] = useState(null);

  const role = userDetails?.role?.toLowerCase();
  const isAdmin = role === "admin" || role === "ad";

  useEffect(() => {
    if (userDetails && !stableUserId) {
      const uid = userDetails.ServiceNo || userDetails.userId || userDetails.empId || "Admin_" + Math.floor(Math.random() * 90000 + 10000);
      setStableUserId(uid);
    }
  }, [userDetails, stableUserId]);

  const enrichAlertData = async (data) => {
    if (data.mobileNo) return data;
    if (!data.driverSno) return data;

    try {
      const driver = users.find(u => u.ServiceNo == data.driverSno);
      if (driver) {
        return {
          ...data,
          driverName: driver.Name || data.driverName,
          mobileNo: driver.MobileNo,
          role: driver.role
        };
      }
    } catch (e) {
      console.warn("Failed to fetch driver details for alert:", e);
    }
    return data;
  };

  // Check for existing emergencies on load
  useEffect(() => {
    if (!isAuthenticated || !isAdmin || !authKey) return;

    const checkActiveEmergencies = async () => {
      try {
        const latestVehicles = await dispatch(fetchVehicles());
        const activeEmergencies = (latestVehicles || []).filter(v => v.ecode && v.ecode.trim() !== "");
        
        if (activeEmergencies.length > 0) {
          const enrichedAlerts = await Promise.all(activeEmergencies.map(enrichAlertData));
          setEmergencyAlerts(enrichedAlerts);

          // Play sound for new alerts
          try {
            setTimeout(() => {
              new Audio("https://actions.google.com/sounds/v1/alarms/alarm_clock.ogg").play().catch(e => console.warn("Audio play failed:", e));
            }, 1000);
          } catch(e) {}
        } else {
          setEmergencyAlerts([]);
        }
      } catch (err) {
        console.error("Failed to check active emergencies:", err);
      }
    };
    
    dispatch(fetchUsers());
    dispatch(fetchVehicles());
    checkActiveEmergencies();
    
    const interval = setInterval(checkActiveEmergencies, 30000);
    return () => clearInterval(interval);
  }, [authKey, isAdmin, isAuthenticated, dispatch]);

  // Connect to Chat/Emergency WebSocket
  useEffect(() => {
    if (!isAuthenticated || !isAdmin || !authKey || !stableUserId) return;

    console.log("🔔 Connecting to WS as:", stableUserId);
    
    chatWS.connect(authKey, stableUserId, "Admin");
    
    const handleWSMessage = async (msg) => {
      if (msg.type === "EMERGENCY_ALERT" || (msg.ecode !== undefined)) {
        if (msg.ecode || msg.ediscription) {
          const enriched = await enrichAlertData(msg);
          
          setEmergencyAlerts(prev => {
            const others = prev.filter(a => a.vehicleId !== msg.vehicleId);
            return [...others, enriched];
          });
          
          try {
            const audio = new Audio("https://actions.google.com/sounds/v1/alarms/alarm_clock.ogg");
            audio.play().catch(e => console.warn(e));
          } catch(e) {}
        } else {
          setEmergencyAlerts(prev => prev.filter(a => a.vehicleId !== msg.vehicleId));
        }
      }
    };

    const unsubscribeChat = chatWS.onMessage(handleWSMessage);

    return () => {
      if (unsubscribeChat) unsubscribeChat();
    };
  }, [authKey, userDetails, isAdmin, isAuthenticated, stableUserId]);

  const handleResolveEmergency = async (alert) => {
    if (!alert || !alert.vehicleId) return;
    
    setResolvingIds(prev => [...prev, alert.vehicleId]);

    try {
      await updateEmergency(alert.vehicleId, "", "");
      
      chatWS.sendEmergency({
        vehicleId: alert.vehicleId,
        ecode: "",
        ediscription: "",
        driverName: alert.driverName
      });

      toast.success("Emergency Alert Acknowledged & Cleared");
      setEmergencyAlerts(prev => prev.filter(a => a.vehicleId !== alert.vehicleId));

    } catch (error) {
      console.error("Failed to clear emergency:", error);
      toast.error("Failed to clear alert status.");
    } finally {
      setResolvingIds(prev => prev.filter(id => id !== alert.vehicleId));
    }
  };

  if (!isAdmin) return null;

  return (
    <EmergencyNotificationModal 
      alerts={emergencyAlerts}
      onResolve={handleResolveEmergency}
      resolvingIds={resolvingIds}
    />
  );
};

export default AdminEmergencyAlert;